import React, { Children, cloneElement, forwardRef, Fragment, memo, useCallback, useRef, useState, } from 'react';
import ContextMenu from '@polestar/component-warehouse-react/components/context-menu/ContextMenu';
import AttachedPopover from '@polestar/component-warehouse-react/components/attached-popover/AttachedPopover';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import styled from '@emotion/styled';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
import { useFocusInitial, useFocusRestore, useFocusTrap, } from '@polestar/component-warehouse-react/hooks/focus';
import useCallbackRef from '@polestar/component-warehouse-react/hooks/useCallbackRef';
import { useUpdateEffect } from '@polestar/component-warehouse-react/hooks/useUpdateEffect';
const WithContextMenu = memo(forwardRef(({ preferredPlacement, forcePlacement, children, onClick, onToggle, offset = Spacings.small, attachIn, ...props }, ref) => {
    const internalRef = useRef();
    const combinedRef = useCombinedRef(internalRef, ref);
    const childElement = Children.only(children);
    const [open, setOpen] = useState(false);
    const [bindRef, childNode] = useCallbackRef();
    const combinedChildRef = useCombinedRef(childElement.props.ref, bindRef);
    const handleChildClick = useCallback((event) => {
        var _a, _b;
        setOpen(!open);
        (_b = (_a = childElement === null || childElement === void 0 ? void 0 : childElement.props) === null || _a === void 0 ? void 0 : _a.onClick) === null || _b === void 0 ? void 0 : _b.call(_a, event);
    }, [childElement.props, open]);
    const handleClose = useCallback(() => {
        setOpen(false);
    }, []);
    const clonedChild = cloneElement(childElement, {
        ref: combinedChildRef,
        onClick: handleChildClick,
        ...aria({
            haspopup: true,
            expanded: open,
        }),
    });
    const handleClick = useCallback((e) => {
        setOpen(false);
        onClick === null || onClick === void 0 ? void 0 : onClick(e);
    }, [onClick]);
    useUpdateEffect(() => {
        onToggle === null || onToggle === void 0 ? void 0 : onToggle(open);
    }, [onToggle, open]);
    useFocusRestore(open, { scrollIntoView: false });
    useFocusInitial(internalRef, open);
    useFocusTrap(internalRef, open);
    return (React.createElement(Fragment, null,
        clonedChild,
        open && (React.createElement(AttachedPopover, { attachTo: childNode, attachIn: attachIn, onClose: handleClose, offset: { top: offset, bottom: offset }, preferredPlacement: preferredPlacement, forcePlacement: forcePlacement },
            React.createElement(StyledContextMenu, { ref: combinedRef, ...props, onClick: handleClick })))));
}));
WithContextMenu.displayName = 'WithContextMenu';
export default WithContextMenu;
const StyledContextMenu = styled(ContextMenu) `
  width: ${createCustomSpacing(15)};
`;
